import React from 'react';
import { Container, Typography, Box, Grid, Stack, Card, CardContent } from '@mui/material';
import { PrecisionManufacturing, Architecture, Shield, Public, BarChart, SettingsInputComponent, ShutterSpeed, Layers } from '@mui/icons-material';
import { motion } from 'framer-motion';

const F1_ORANGE = '#FF6B00';
const BORDER_COLOR = '#E6E6E6';

const IMG_RACKING_HERO = "https://images.pexels.com/photos/1797418/pexels-photo-1797418.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_RACKING_DETAIL = "https://images.pexels.com/photos/4481258/pexels-photo-4481258.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_RACKING_AISLE = "https://images.pexels.com/photos/1797428/pexels-photo-1797428.jpeg?auto=compress&cs=tinysrgb&w=1600";
const IMG_STEEL_BAYS = "https://images.pexels.com/photos/4481323/pexels-photo-4481323.jpeg?auto=compress&cs=tinysrgb&w=1600";

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function ModuraxRefinedFeed() {
  return (
    <Box className="circuit-bg" sx={{ bgcolor: 'white', minHeight: '100vh', color: '#111', overflowX: 'hidden' }}>
      
      {/* SECTION 1: RESPONSIVE HERO */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: { xs: 'auto', md: '80vh' }, borderBottom: `1px solid ${BORDER_COLOR}`, position: 'relative' }}>
        <Box component={motion.div} initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} sx={{ 
          width: { xs: '100%', md: '140px' }, borderRight: { md: `1px solid ${BORDER_COLOR}` },
          borderBottom: { xs: `1px solid ${BORDER_COLOR}`, md: 'none' }, display: 'flex', 
          flexDirection: { xs: 'row', md: 'column' }, alignItems: 'center', 
          justifyContent: { xs: 'center', md: 'flex-start' }, py: { xs: 2, md: 4 }, bgcolor: '#fff'
        }}>
          <Typography sx={{ 
            writingMode: { xs: 'horizontal-tb', md: 'vertical-rl' }, transform: { md: 'rotate(180deg)' }, 
            fontSize: { xs: '2rem', md: '4rem', lg: '5.5rem' }, fontWeight: 950, letterSpacing: { xs: -1, md: -4 }, textTransform: 'uppercase'
          }}>
            <span style={{ color: F1_ORANGE }}>MODU</span>RAX
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, position: 'relative', display: 'flex', alignItems: 'center', py: { xs: 6, md: 0 } }}>
          <Box 
            component="img" 
            src={IMG_RACKING_HERO} 
            sx={{ 
              position: 'absolute', 
              right: 0, 
              top: 0, 
              width: { xs: '100%', md: '60%' }, 
              height: '100%', 
              objectFit: 'cover', 
              filter: 'grayscale(100%) opacity(0.12)', 
              zIndex: 1,
              transition: 'all 0.5s ease',
              '&:hover': {
                filter: 'grayscale(0%) opacity(0.8)',
              }
            }} 
          />
          <Container maxWidth="lg" component={motion.div} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} sx={{ zIndex: 2, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ borderLeft: `6px solid ${F1_ORANGE}`, pl: { xs: 2, md: 4 } }}>
                <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 900, color: F1_ORANGE }}>SYSTEM ARCHITECTURE v4.2</Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.1, mt: 1 }}>HIGH-BAY <br />INFRASTRUCTURE</Typography>
                <Typography variant="body1" sx={{ mt: 2, color: '#555', maxWidth: '580px', lineHeight: 1.7 }}>
                    Modurax delivers high-density storage frameworks engineered for the extreme requirements of modern global commerce. 
                    Our structural systems are designed to withstand the dynamic loads of high-speed ASRS operations while maintaining 
                    millimetric deflection tolerances across vertical heights exceeding 30 meters.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} sx={{ mt: 5 }}>
                    <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>S355JR</Typography><Typography variant="caption" sx={{ color: '#999', fontWeight: 700 }}>STEEL GRADE</Typography></Box>
                    <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>4,500KG</Typography><Typography variant="caption" sx={{ color: '#999', fontWeight: 700 }}>MAX PAYLOAD</Typography></Box>
                </Stack>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* SECTION 2: REDESIGNED MATERIAL SPECS (CARD GRID) */}
      <Box sx={{ py: { xs: 6, md: 12 }, bgcolor: '#f4f4f4' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={containerVariants}>
            <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: -2, mb: 1, fontSize: { xs: '1.75rem', md: '3rem' } }}>MATERIAL SPECS</Typography>
                <Typography variant="body2" sx={{ color: '#666', maxWidth: '700px', mx: 'auto', lineHeight: 1.8 }}>
                    Modurax utilizes S355JR high-tensile structural steel, cold-rolled via proprietary automated lines. 
                    Each batch undergoes 100% ultrasonic non-destructive testing (NDT) to ensure zero internal fissures, 
                    guaranteeing integrity under static and dynamic stress cycles in high-velocity hubs.
                </Typography>
            </Box>

            <Grid container spacing={3}>
              {[
                { l: "YIELD STRENGTH", v: "355 N/mm²", d: "Minimum plastic deformation threshold", icon: <BarChart /> },
                { l: "ELASTICITY MODULUS", v: "210,000 N/mm²", d: "Rigidity under variable load states", icon: <SettingsInputComponent /> },
                { l: "GALVANIC COATING", v: "ISO 1461", d: "Anti-corrosive molten zinc bath", icon: <Layers /> },
                { l: "BUCKLING RES.", v: "L/200 NORM", d: "Structural verticality tolerance", icon: <ShutterSpeed /> }
              ].map((spec, i) => (
                <Grid item xs={12} sm={6} lg={3} key={i}>
                  <Card component={motion.div} variants={itemVariants} whileHover={{ y: -10, boxShadow: '0px 20px 40px rgba(0,0,0,0.1)' }} sx={{ height: '100%', border: '1px solid #ddd', borderRadius: 0, position: 'relative', overflow: 'visible' }}>
                    <Box sx={{ position: 'absolute', top: -15, left: 20, bgcolor: F1_ORANGE, color: 'white', p: 1, display: 'flex', alignItems: 'center' }}>
                       {spec.icon}
                    </Box>
                    <CardContent sx={{ pt: 5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 900, color: '#999', display: 'block', mb: 1 }}>{spec.l}</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 900, color: '#111', mb: 1 }}>{spec.v}</Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.4, fontWeight: 500 }}>{spec.d}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* EXPANDED TECHNICAL DATA CARDS */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
                {[
                    { t: "OMEGA PROFILE TECHNOLOGY", d: "Our 12-bend structural geometry provides a 28% increase in torsional rigidity over standard C-profiles, allowing for heights up to 30m in ASRS environments without secondary bracing." }, 
                    { t: "SEISMIC FORCE DAMPING", d: "Integrated X-bracing and oversized high-tensile base plates are specifically engineered for high-magnitude seismic Zone 4 damping and lateral energy dissipation." }, 
                    { t: "PROPRIETARY AUTO-LOCK", d: "Features our 5-hook beam connectors with secondary gravitational safety pins that prevent accidental forklift dislodgement during high-speed vertical retrieval cycles." }, 
                    { t: "QR ASSET HEAT-MAP", d: "Every structural member is laser-engraved with mill-traceable QR codes, allowing logistics managers to access real-time asset life-cycle and structural maintenance data." }
                ].map((item, i) => (
                  <Grid item xs={12} md={6} key={i}>
                    <Box component={motion.div} variants={itemVariants} sx={{ p: 4, bgcolor: 'white', borderLeft: `4px solid ${F1_ORANGE}`, height: '100%' }}>
                      <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>{item.t}</Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.8 }}>{item.d}</Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* SECTION 3: TECHNICAL DETAIL */}
      <Box sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg" component={motion.div} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box 
                component="img" 
                src={IMG_RACKING_DETAIL} 
                sx={{ 
                  width: '100%', 
                  height: { xs: 220, sm: 300, md: 400 }, 
                  objectFit: 'cover', 
                  filter: 'grayscale(100%)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                  }
                }} 
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 3, fontSize: { xs: '1.5rem', md: '3rem' } }}>CAPACITY ANALYTICS</Typography>
              <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8 }}>
                Utilizing Finite Element Analysis (FEA), Modurax optimizes the steel gauge distribution for every project, 
                minimizing environmental footprint while maximizing safety. Our Selective, Double Deep, and VNA (Very Narrow Aisle) 
                configurations share a common structural backbone, allowing for seamless facility reconfiguration as inventory 
                profiles evolve over time.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 4: AISLE DATA */}
      <Box sx={{ py: { xs: 6, md: 12 }, bgcolor: '#fafafa' }}>
        <Container maxWidth="lg" component={motion.div} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h2" sx={{ fontWeight: 900, color: F1_ORANGE, fontSize: { xs: '2.5rem', md: '3.75rem' } }}>1,240+</Typography>
              <Typography variant="h5" sx={{ fontWeight: 950, mb: 2, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>GLOBAL DEPLOYMENTS</Typography>
              <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8 }}>
                Modurax operates a standardized engineering protocol across 42 countries. Our systems are strictly compliant 
                with EN 15512, RMI (USA), and SEMA (UK) regulations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box 
                component="img" 
                src={IMG_RACKING_AISLE} 
                sx={{ 
                  width: '100%', 
                  height: { xs: 220, sm: 320, md: 450 }, 
                  objectFit: 'cover', 
                  filter: 'grayscale(100%)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                  }
                }} 
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* SECTION 5: FINAL ASSETS */}
      <Box sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 4, md: 10 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ fontWeight: 900, mb: 3, fontSize: { xs: '1.35rem', md: '2.125rem' } }}>OPERATIONAL LIFECYCLE</Typography>
              <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8 }}>
                The Modurax "Circular Design" philosophy ensures that every component is 100% recyclable. 
                Our high-speed boltless assembly systems reduce on-site deployment time by up to 40%.
              </Typography>
              {/* <Box sx={{ mt: 5, p: 3, border: '4px solid #111', display: 'inline-block' }}>
                 <Typography variant="h6" sx={{ fontWeight: 900 }}>ENGINEERING@MODURAX.COM</Typography>
              </Box> */}
            </Grid>
            <Grid item xs={12} md={5}>
               <Box 
                component="img" 
                src={IMG_STEEL_BAYS} 
                sx={{ 
                  width: '100%', 
                  height: { xs: 200, sm: 280, md: 350 }, 
                  objectFit: 'cover', 
                  filter: 'grayscale(100%)',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    filter: 'grayscale(0%)',
                  }
                }} 
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}